import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AVAILABLE_QUERY_FILTERS, AvailableQueryFilters, DEFAULT_FILTERS_FORM_STATE } from '@shared';
import { arrayToQueryString, queryStringToArrayNumber, queryStringToBoolean } from '../utils/parsers.util';

type UrlFilters = Partial<Record<AvailableQueryFilters, any>>;

@Injectable({
  providedIn: 'root',
})
export class UrlFiltersService {
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  private activeFilters = signal<AvailableQueryFilters[]>(AVAILABLE_QUERY_FILTERS);

  private parsers: Partial<Record<AvailableQueryFilters, (urlValue: string) => any>> = {
    category: queryStringToArrayNumber,
    brandIds: queryStringToArrayNumber,
    isAvailablePointsRange: queryStringToBoolean,
    sortBy: String,
    scroll: Number,
    itemsLoaded: Number,
  };

  private serializers: Partial<Record<AvailableQueryFilters, (array: string[] | number[]) => string>> = {
    category: arrayToQueryString,
    brandIds: arrayToQueryString,
    isAvailablePointsRange: String,
    sortBy: String,
    scroll: String,
    itemsLoaded: String,
  };

  private getQueryParams() {
    return new URLSearchParams(window.location.search);
  }

  filters(): UrlFilters {
    const { queryParams } = this.activatedRoute.snapshot;
    const filters = this.activeFilters().reduce((acc, filter) => {
      if (queryParams[filter]?.length) {
        acc[filter] = this.parsers[filter] ? this.parsers[filter](queryParams[filter]) : queryParams[filter];
      }
      return acc;
    }, {} as UrlFilters);
    return filters;
  }

  setFilters(filters: UrlFilters): void {
    const searchParams = this.getQueryParams();
    this.activeFilters().forEach((filter) => {
      if (searchParams.has(filter)) {
        searchParams.delete(filter);
      }
      if (this.hasValue(filter, filters[filter]))
        searchParams.set(
          filter,
          this.serializers[filter] ? this.serializers[filter](filters[filter]) : filters[filter],
        );
    });
    this.replaceUrl(searchParams);
  }

  clearFilters(): void {
    const searchParams = this.getQueryParams();
    this.activeFilters().forEach((filter) => {
      if (searchParams.has(filter)) {
        searchParams.delete(filter);
      }
    });
    this.replaceUrl(searchParams);
  }

  // Empty lists and the default sorting add nothing to the URL, so they are left out of it.
  private hasValue(filter: AvailableQueryFilters, value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (filter === 'sortBy') {
      return !!value && value !== DEFAULT_FILTERS_FORM_STATE.sortBy;
    }
    return !!value;
  }

  private replaceUrl(searchParams: URLSearchParams): void {
    const query = searchParams.size ? `?${searchParams}` : '';
    window.history.replaceState({}, '', `${window.location.pathname}${query}`);
  }
}
